import React, { useState, useMemo } from 'react';
import { getBaseUrl } from '../config';

const TableSidebar = ({ tables, loading, error, selectedTable, onTableSelect, onTablesUpdate, authTables = [], onAuthClick }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [addTableModal, setAddTableModal] = useState(false);
  const [newTableName, setNewTableName] = useState('');
  const [addTableLoading, setAddTableLoading] = useState(false);

  // Filter tables based on search term
  const filteredTables = useMemo(() => {
    if (!tables || !searchTerm.trim()) {
      return tables || [];
    }
    return tables.filter(table => 
      table.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [tables, searchTerm]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleAddTableClick = () => {
    setAddTableModal(true);
    setNewTableName('');
  };

  const handleAddTableSubmit = async (e) => {
    e.preventDefault();
    if (!newTableName.trim()) return;

    setAddTableLoading(true);
    try {
      const response = await fetch(`${getBaseUrl()}/api/${newTableName.trim()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sample_column: 'Sample Value'
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to create table: ${response.status}`);
      }

      // Close modal and refresh tables
      setAddTableModal(false);
      setNewTableName('');
      
      // Notify parent to refresh tables list
      if (onTablesUpdate) {
        onTablesUpdate();
      }

    } catch (err) {
      alert(`Create table failed: ${err.message}`);
    } finally {
      setAddTableLoading(false);
    }
  };

  const handleAddTableCancel = () => {
    setAddTableModal(false);
    setNewTableName('');
  };

  return (
    <div className="column is-3">
      <div className="panel">
        <div className="panel-heading" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Tables</span>
          <button
            className="button is-small is-info"
            onClick={handleAddTableClick}
            title="Add Table"
            style={{ padding: '0.25rem 0.5rem' }}
          >
            <span className="icon is-small">
              <i className="bi bi-plus"></i>
            </span>
            <span>Add Table</span>
          </button>
        </div>
        
        {/* Search Input */}
        {!loading && !error && tables && tables.length > 0 && (
          <div className="panel-block">
            <p className="control has-icons-left">
              <input
                className="input is-small"
                type="text"
                placeholder="Search tables..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
              <span className="icon is-small is-left">
                <i className="bi bi-search"></i>
              </span>
            </p>
          </div>
        )}

        {loading && (
          <div className="panel-block">
            <div className="control">
              <button className="button is-loading is-static">Loading</button>
            </div>
          </div>
        )}
        {error && (
          <div className="panel-block">
            <div className="notification is-danger is-light">
              Error: {error}
            </div>
          </div>
        )}
        
        {/* Display filtered tables */}
        {!loading && !error && tables && tables.length > 0 && filteredTables.map((table, index) => (
          <a
            key={index}
            className={`panel-block ${selectedTable === table ? 'is-active' : ''}`}
            onClick={() => onTableSelect(table)}
            style={{ cursor: 'pointer' }}
          >
            <span className="panel-icon">
              <i className="fas fa-table" aria-hidden="true"></i>
            </span>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <span>{table}</span>
              {authTables.includes(table) && (
                <span 
                  className="icon is-small has-text-warning" 
                  title="Authentication Table (Click to test login)"
                  onClick={(e) => {
                    e.stopPropagation();
                    onAuthClick(table);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <i className="bi bi-lock-fill"></i>
                </span>
              )}
            </div>
          </a>
        ))}
        
        {/* Show message when search has no results */}
        {!loading && !error && tables && tables.length > 0 && filteredTables.length === 0 && searchTerm.trim() && (
          <div className="panel-block">
            <div className="content">
              <p>No tables found matching "{searchTerm}".</p>
            </div>
          </div>
        )}
        
        {/* Show message when no tables exist */}
        {!loading && !error && (!tables || tables.length === 0) && (
          <div className="panel-block">
            <div className="content">
              <p>No tables found. You may like to know how to <a href="https://injee.codeberg.page/docs/#/create_books" target="_blank" rel="noopener noreferrer">create one</a>.</p>
            </div>
          </div>
        )}
      </div>

      {/* Add Table Modal */}
      <div className={`modal ${addTableModal ? 'is-active' : ''}`}>
        <div className="modal-background" onClick={handleAddTableCancel}></div>
        <div className="modal-card">
          <header className="modal-card-head">
            <p className="modal-card-title">Add New Table</p>
            <button
              className="delete"
              aria-label="close"
              onClick={handleAddTableCancel}
            ></button>
          </header>
          <form onSubmit={handleAddTableSubmit}>
            <section className="modal-card-body">
              <div className="field">
                <label className="label">Table Name</label>
                <div className="control">
                  <input
                    className="input"
                    type="text"
                    placeholder="Enter table name"
                    value={newTableName}
                    onChange={(e) => setNewTableName(e.target.value)}
                    required
                  />
                </div>
                <p className="help">The table will be created with a sample column containing "Sample Value".</p>
              </div>
            </section>
            <footer className="modal-card-foot">
              <button
                className={`button is-success ${addTableLoading ? 'is-loading' : ''}`}
                type="submit"
                disabled={addTableLoading || !newTableName.trim()}
              >
                Create Table
              </button>
              <button
                className="button"
                type="button"
                onClick={handleAddTableCancel}
                disabled={addTableLoading}
              >
                Cancel
              </button>
            </footer>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TableSidebar;